import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Search, Trash2, X, Upload, Receipt, ChevronLeft, ChevronRight } from 'lucide-react';

const CATEGORIES = ['Materials', 'Tools & Equipment', 'Vehicle', 'Fuel', 'Subcontractor', 'Insurance', 'Rent & Utilities', 'Office', 'Advertising', 'Professional Services', 'Other'];

export default function ExpensesPage() {
  const { api } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/expenses?page=${page}&per_page=50&search=${search}`);
      setExpenses(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/suppliers').then(r => setSuppliers(r.data || [])).catch(() => {}); }, [api]);

  const deleteExpense = async (id) => {
    try { await api.delete(`/expenses/${id}`); load(); } catch (e) { alert('Failed'); }
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    try { const [y,m,dd] = d.slice(0,10).split('-'); return `${dd}/${m}/${y}`; } catch { return d; }
  };

  return (
    <div data-testid="expenses-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-[Manrope]">Expenses</h1>
          <p className="text-sm text-slate-500 mt-1">{total} expense{total !== 1 ? 's' : ''}</p>
        </div>
        <button data-testid="add-expense-btn" onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-semibold hover:bg-[#A04000]"><Plus size={14} /> Add Expense</button>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search expenses..." className="w-full pl-10 pr-4 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
      </div>

      <div className="border border-[#E2E2DF] rounded-xl overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-[#E2E2DF]">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Date</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Supplier</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Description</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Category</th>
              <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Excl GST</th>
              <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-slate-500">GST</th>
              <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Total</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {expenses.map(e => (
              <tr key={e._id} className="border-t border-[#E2E2DF] hover:bg-slate-50 cursor-pointer" onClick={() => { setEditing(e); setShowForm(true); }}>
                <td className="px-4 py-2.5 text-slate-600">{fmtDate(e.expense_date)}</td>
                <td className="px-4 py-2.5 font-medium text-slate-800">{e.supplier_name || '-'}</td>
                <td className="px-4 py-2.5 text-slate-600">{e.description}</td>
                <td className="px-4 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{e.category || '-'}</span></td>
                <td className="px-4 py-2.5 text-right font-mono-num">${e.amount_excl_gst?.toFixed(2)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-[#D35400]">${e.gst_amount?.toFixed(2)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num font-semibold">${e.amount_incl_gst?.toFixed(2)}</td>
                <td className="px-4 py-2.5 text-right">
                  {e.receipt_image && <Receipt size={14} className="inline text-green-500 mr-1" title="Has receipt" />}
                  <button onClick={(ev) => { ev.stopPropagation(); deleteExpense(e._id); }} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {expenses.length === 0 && !loading && (
              <tr><td colSpan={8} className="text-center py-10 text-slate-400">No expenses yet.</td></tr>
            )}
          </tbody>
        </table>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E2DF]">
            <span className="text-xs text-slate-500">{total} expenses</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1.5 rounded border border-[#E2E2DF] hover:bg-slate-50 disabled:opacity-30"><ChevronLeft size={16} /></button>
              <span className="text-xs text-slate-500">{page}/{pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1.5 rounded border border-[#E2E2DF] hover:bg-slate-50 disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {showForm && <ExpenseForm api={api} expense={editing} suppliers={suppliers} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}


function ExpenseForm({ api, expense, suppliers, onClose, onSaved }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    description: '', category: 'Materials', supplier_name: '', supplier_gst_number: '',
    invoice_date: new Date().toISOString().slice(0, 10), paid_date: '',
    amount: '', is_gst_inclusive: true, has_gst: true, gst_rate: 15,
    receipt_image: '', notes: '',
    ...(expense || {}),
  });
  const [saving, setSaving] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState(expense?.supplier_name || '');
  const [showSuppliers, setShowSuppliers] = useState(false);

  const filteredSuppliers = suppliers.filter(s => s.name.toLowerCase().includes(supplierSearch.toLowerCase()));

  const selectSupplier = (s) => {
    setForm(f => ({ ...f, supplier_name: s.name, supplier_gst_number: s.gst_number || f.supplier_gst_number }));
    setSupplierSearch(s.name);
    setShowSuppliers(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, receipt_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const amount = parseFloat(form.amount) || 0;
  const gstRate = parseFloat(form.gst_rate) || 15;
  const gstAmt = form.has_gst ? (form.is_gst_inclusive ? amount - (amount / (1 + gstRate / 100)) : amount * (gstRate / 100)) : 0;
  const exclGst = form.has_gst ? (form.is_gst_inclusive ? amount - gstAmt : amount) : amount;
  const inclGst = form.has_gst ? (form.is_gst_inclusive ? amount : amount + gstAmt) : amount;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0, supplier_name: supplierSearch || form.supplier_name };
      if (expense?._id) {
        await api.put(`/expenses/${expense._id}`, payload);
      } else {
        await api.post('/expenses', payload);
      }
      onSaved();
    } catch (e) { alert('Failed to save expense'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div data-testid="expense-form-modal" className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-800">{expense ? 'Edit Expense' : 'Add Expense'}</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          {/* Supplier */}
          <div className="relative">
            <label className="text-xs font-medium text-slate-500 block mb-1">Supplier / Business</label>
            <input data-testid="expense-supplier" value={supplierSearch} onChange={e => { setSupplierSearch(e.target.value); setForm(f => ({...f, supplier_name: e.target.value})); setShowSuppliers(true); }} placeholder="Type or search supplier..." className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
            {showSuppliers && filteredSuppliers.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-[#E2E2DF] rounded-lg shadow-lg max-h-36 overflow-y-auto">
                {filteredSuppliers.slice(0, 10).map(s => (
                  <button key={s._id} onClick={() => selectSupplier(s)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 border-b border-[#E2E2DF] last:border-0">
                    {s.name} {s.gst_number && <span className="text-xs text-slate-400 ml-2">GST: {s.gst_number}</span>}
                  </button>
                ))}
              </div>
            )}
            {showSuppliers && <div className="fixed inset-0 z-10" onClick={() => setShowSuppliers(false)} />}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Supplier GST Number</label>
              <input data-testid="expense-gst-number" value={form.supplier_gst_number} onChange={e => setForm(f => ({...f, supplier_gst_number: e.target.value}))} placeholder="Auto-filled from supplier" className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 bg-white">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Description</label>
            <input data-testid="expense-description" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="What was purchased" className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Invoice Date</label>
              <input type="date" value={form.invoice_date} onChange={e => setForm(f => ({...f, invoice_date: e.target.value}))} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Paid Date</label>
              <input type="date" value={form.paid_date} onChange={e => setForm(f => ({...f, paid_date: e.target.value}))} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
            </div>
          </div>

          {/* Amount & GST */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-3 items-end">
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Amount ($)</label>
                <input data-testid="expense-amount" type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 font-mono-num" placeholder="0.00" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Amount is</label>
                <select value={form.is_gst_inclusive ? 'inclusive' : 'exclusive'} onChange={e => setForm(f => ({...f, is_gst_inclusive: e.target.value === 'inclusive'}))} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 bg-white">
                  <option value="inclusive">GST Inclusive</option>
                  <option value="exclusive">GST Exclusive</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={form.has_gst} onChange={e => setForm(f => ({...f, has_gst: e.target.checked}))} className="rounded" />
                  GST Applicable
                </label>
              </div>
            </div>
            {form.has_gst && (
              <div className="mt-3 pt-3 border-t border-[#E2E2DF] grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-xs text-slate-400">Excl. GST:</span><p className="font-semibold font-mono-num">${exclGst.toFixed(2)}</p></div>
                <div><span className="text-xs text-slate-400">GST ({gstRate}%):</span><p className="font-semibold font-mono-num text-[#D35400]">${gstAmt.toFixed(2)}</p></div>
                <div><span className="text-xs text-slate-400">Incl. GST:</span><p className="font-semibold font-mono-num">${inclGst.toFixed(2)}</p></div>
              </div>
            )}
          </div>

          {/* Receipt Upload */}
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Receipt / Invoice Image</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            {form.receipt_image ? (
              <div className="relative inline-block">
                <img src={form.receipt_image} alt="Receipt" className="max-h-32 rounded-lg border border-[#E2E2DF]" />
                <button onClick={() => setForm(f => ({...f, receipt_image: ''}))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X size={12} /></button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-[#E2E2DF] rounded-lg text-sm text-slate-500 hover:border-[#D35400] hover:text-[#D35400] transition-colors">
                <Upload size={16} /> Upload receipt or invoice image
              </button>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button data-testid="save-expense-btn" onClick={save} disabled={saving || !form.amount} className="flex-1 px-4 py-2.5 bg-[#D35400] text-white rounded-lg text-sm font-semibold hover:bg-[#A04000] disabled:opacity-50">{saving ? 'Saving...' : expense ? 'Update Expense' : 'Save Expense'}</button>
          <button onClick={onClose} className="px-4 py-2.5 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}
